import { Typography } from '@euler/app/components/typography';
import { Panel } from '@euler/app/tv/components/Panel';
import { SearchCriteriaNavParams } from '@euler/app/tv/components/search/SearchCriteriaContainer';
import { CriterionOptionsSelectorProps } from '@euler/app/tv/components/search/SearchCriterionOptionSelector';
import { SearchBookmarksManager } from '@euler/app/tv/functions/SearchBookmarksManager';
import { useSearchCriteriaContext } from '@euler/app/tv/functions/SearchCriteriaContext';
import { getAllCriterionProviders } from '@euler/app/tv/functions/search-criteria/factory';
import {
  SearchCriteria,
  SearchCriterionType,
  SearchCriterionValueType,
} from '@euler/app/tv/functions/search-criteria/types';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Icon } from '@euler/components/Icomoon';
import { MaybeText } from '@euler/components/MaybeText';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { ReactNode, memo, useCallback, useMemo } from 'react';
import {
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

export const SearchCriteriaScreen = wrapNavigatorScreen(
  () => {
    const navigation =
      useNavigation<StackNavigationProp<SearchCriteriaNavParams>>();

    const {
      controller: { form },
      reset,
    } = useSearchCriteriaContext();

    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();

    const providers = useMemo(() => getAllCriterionProviders(), []);

    const onCellPress = useCallback(
      <T extends SearchCriterionType>(type: T) => {
        const props = {
          criterionType: type,
          selectedValue: form[type].value,
          onPress: (value: SearchCriterionValueType<T> | undefined) => {
            form[type].update(value as any);
            navigation.goBack();
          },
        } as CriterionOptionsSelectorProps<T, SearchCriterionValueType<T>>;
        navigation.navigate('_options', props as any);
      },
      [form, navigation],
    );

    const onReset = useCallback(() => {
      reset();
    }, [reset]);

    const onBookmark = useCallback(async () => {
      const criteria: SearchCriteria = {} as any;
      for (const provider of providers) {
        const value = form[provider.type].value;
        (criteria as any)[provider.type] = value as any;
      }
      await SearchBookmarksManager.shared.saveSearchCriterionAsBookmark(
        criteria,
      );
    }, [form, providers]);

    return (
      <Panel
        title="条件筛选"
        contentContainerStyle={{ flex: 1 }}
        headerStyle={{ marginTop: rpx(20) }}
        titleStyle={{ fontSize: rpx(28), lineHeight: rpx(44) }}
        headerRight={
          <HeaderRight
            onResetButtonPress={onReset}
            onBookmarkButtonPress={onBookmark}
          />
        }
        css={`
          flex: 1;
        `}
      >
        <View
          css={`
            flex: 1;
            border-width: 1px;
            border-color: ${theme.search.criterion.optionBorderColor};
            border-radius: ${rpx(8)}px;
            overflow: hidden;
          `}
        >
          <ScrollView
            contentContainerStyle={{
              flexGrow: 1,
            }}
          >
            {providers.map((provider, index) => (
              <Cell
                key={provider.type}
                type={provider.type}
                label={provider.getLabel()}
                value={provider.getDisplayText(form[provider.type].value)}
                image={provider.getDisplayImage(form[provider.type].value)}
                first={index === 0}
                onPress={onCellPress}
              />
            ))}
          </ScrollView>
        </View>
      </Panel>
    );
  },
  {
    cardStyle: {
      backgroundColor: 'transparent',
    },
  },
);

const Cell = memo(
  ({
    type,
    label,
    image,
    value,
    first,
    onPress,
  }: {
    type: SearchCriterionType;
    label: string;
    value?: ReactNode;
    image?: string;
    first?: boolean;
    onPress?: (type: SearchCriterionType) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const {
      search: {
        criterion: {
          optionLabelTextColor,
          optionValueTextColor,
          optionBorderColor,
          optionArrowColor,
        },
      },
    } = usePresentationTheme();

    const handlePress = useCallback(() => {
      onPress?.(type);
    }, [onPress, type]);

    return (
      <TouchableOpacity
        css={`
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          border-top-width: 1px;
          border-top-color: ${first ? 'transparent' : optionBorderColor};
          padding-left: ${rpx(20)}px;
          padding-right: ${rpx(20)}px;
        `}
        onPress={handlePress}
      >
        <Typography.Label
          medium
          size={rpx(21)}
          lineHeight={rpx(48)}
          color={optionLabelTextColor}
        >
          {label}
        </Typography.Label>
        <HStack justifyContent="flex-end">
          {image != null ? (
            <Img
              uri={image}
              css={`
                width: ${rpx(24)}px;
                height: ${rpx(24)}px;
                margin-right: ${rpx(12)}px;
              `}
            />
          ) : null}
          <MaybeText
            text={value}
            css={`
              font-family: ${FontFamily.NotoSans.Light};
              font-size: ${rpx(21)}px;
              line-height: ${rpx(48)}px;
              color: ${optionValueTextColor};
            `}
          />
          <Icon
            name="chevron-thin-right"
            size={rpx(20)}
            color={optionArrowColor}
            css={`
              margin-left: ${rpx(8)}px;
            `}
          />
        </HStack>
      </TouchableOpacity>
    );
  },
);

const HeaderRight = memo(
  ({
    onResetButtonPress,
    onBookmarkButtonPress,
    style,
  }: {
    onBookmarkButtonPress?: () => void;
    onResetButtonPress?: () => void;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    return (
      <HStack justifyContent="flex-end">
        <TouchableOpacity
          style={style}
          onPress={onBookmarkButtonPress}
          hitSlop={PredefinedHitSlops.medium}
        >
          <Typography.Label
            light
            size={rpx(20)}
            color={theme.panel.headerLinkTextColor}
          >
            设为常用
          </Typography.Label>
        </TouchableOpacity>
        <TouchableOpacity
          style={style}
          onPress={onResetButtonPress}
          hitSlop={PredefinedHitSlops.medium}
          css={`
            margin-left: ${rpx(20)}px;
          `}
        >
          <Typography.Label
            light
            size={rpx(20)}
            color={theme.panel.headerLinkTextColor}
          >
            重置条件
          </Typography.Label>
        </TouchableOpacity>
      </HStack>
    );
  },
);
